
import React, { useRef, useEffect } from "react";
import { initializeCanvasScripts } from "@/lib/htmlUtils";
import { GeneratedQuestion } from "@/services/questionGeneratorService";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Loader2, Copy, CheckCircle2, AlertTriangle } from "lucide-react";

interface GeneratedQuestionsListProps {
  questions: GeneratedQuestion[];
  selectAll: boolean;
  onToggleSelectAll: () => void;
  onToggleQuestion: (id: string) => void;
  onSetCorrectOption: (questionId: string, optionId: string) => void;
  onSaveQuestions: () => Promise<void>;
  onShowVideoModal: () => void;
  isSaving: boolean;
  topic?: { title: string; content: string } | null;
  onUpdateTopic?: (updatedTopic: { title: string; content: string }) => void;
}

const GeneratedQuestionsList: React.FC<GeneratedQuestionsListProps> = ({
  questions,
  selectAll,
  onToggleSelectAll,
  onToggleQuestion,
  onSetCorrectOption,
  onSaveQuestions,
  onShowVideoModal,
  isSaving,
  topic,
  onUpdateTopic,
}) => {
  const [topicTitle, setTopicTitle] = React.useState(topic?.title || "");
  const [topicContent, setTopicContent] = React.useState(topic?.content || "");
  const [isTopicDialogOpen, setIsTopicDialogOpen] = React.useState(false);

  // Once the DOM is updated with questions that may contain canvas elements,
  // call the initializer to ensure canvases and scripts are properly set up
  useEffect(() => {
    if (questions.length > 0) {
      setTimeout(() => {
        initializeCanvasScripts();
      }, 500);
    }
  }, [questions]);

  // Make sure each question content div has a data-question-id attribute
  // Here's how to modify the question content container in the render method:

  // Ensure the initializeCanvasScripts function is called after component mount
  useEffect(() => {
    initializeCanvasScripts();
  }, []);

  const handleUpdateTopic = () => {
    if (onUpdateTopic) {
      onUpdateTopic({ title: topicTitle, content: topicContent });
      setIsTopicDialogOpen(false);
    }
  };

  return (
    <div>
      {topic && (
        <Card className="mb-4">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-base">
              Topic: {topic.title}
            </CardTitle>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsTopicDialogOpen(true)}
                  >
                    Edit Topic
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Edit topic title and content</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardHeader>
          <CardContent>
            <div dangerouslySetInnerHTML={{ __html: topic.content }} />
          </CardContent>
        </Card>
      )}

      <Dialog open={isTopicDialogOpen} onOpenChange={setIsTopicDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit Topic</DialogTitle>
            <DialogDescription>
              Make changes to the topic title and content here. Click save when
              you're done.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="title" className="text-right">
                Title
              </Label>
              <Input
                id="title"
                value={topicTitle}
                onChange={(e) => setTopicTitle(e.target.value)}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="content" className="text-right">
                Content
              </Label>
              <Textarea
                id="content"
                value={topicContent}
                onChange={(e) => setTopicContent(e.target.value)}
                className="col-span-3 min-h-[100px]"
              />
            </div>
          </div>
          <Button onClick={handleUpdateTopic}>Save changes</Button>
        </DialogContent>
      </Dialog>

      {questions.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="select-all"
              checked={selectAll}
              onCheckedChange={onToggleSelectAll}
            />
            <Label htmlFor="select-all" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed">
              Select All
            </Label>
          </div>
        </div>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[50px]">Select</TableHead>
            <TableHead>Question</TableHead>
            <TableHead>Options</TableHead>
            <TableHead>Explanation</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {questions.map((question) => (
            <TableRow key={question.id}>
              <TableCell className="font-medium">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={`select-${question.id}`}
                    checked={question.selected}
                    onCheckedChange={() => onToggleQuestion(question.id)}
                  />
                </div>
              </TableCell>
              <TableCell>
                <div
                  className="question-content"
                  data-question-id={question.id}
                  dangerouslySetInnerHTML={{ __html: question.text }}
                />
                {question.isDuplicate && (
                  <div className="flex items-center text-sm text-orange-500 mt-2">
                    <AlertTriangle className="h-4 w-4 mr-1" />
                    Duplicate question
                  </div>
                )}
              </TableCell>
              <TableCell>
                <Table>
                  <TableBody>
                    {question.options.map((option) => (
                      <TableRow key={option.id}>
                        <TableCell className="w-[50px]">
                          <div className="flex">
                            <input
                              type="radio"
                              name={`option-group-${question.id}`}
                              checked={option.isCorrect}
                              onChange={() => onSetCorrectOption(question.id, option.id)}
                              className="mr-2"
                            />
                          </div>
                        </TableCell>
                        <TableCell>
                          <Label
                            htmlFor={`option-${option.id}`}
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed"
                          >
                            {option.text}
                          </Label>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableCell>
              <TableCell>{question.explanation}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {questions.length > 0 && (
        <div className="flex justify-end space-x-2 mt-4">
          <Button onClick={onShowVideoModal}>Generate Video</Button>
          <Button
            onClick={onSaveQuestions}
            disabled={isSaving}
          >
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isSaving ? "Saving..." : "Save Selected Questions"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default GeneratedQuestionsList;

// Removed the custom RadioGroup components that were causing type errors
